import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle, KeyRound, Mail } from 'lucide-react';
import { authAPI } from '../services/api';

const inp = {
  width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8,
  fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box', color: '#111827',
  backgroundColor: 'white',
};
const labelS = { display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: 5 };
const btnPrimary = {
  width: '100%', padding: '11px', borderRadius: 8, backgroundColor: '#111827', color: 'white',
  border: 'none', fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
};

const Spinner = () => (
  <div style={{ width: 18, height: 18, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Mode "demande de reset" (pas de token en URL)
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  // Mode "saisie nouveau mot de passe" (token en URL)
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setRequestSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur. Veuillez réessayer.');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) return setError('Les mots de passe ne correspondent pas.');
    if (password.length < 8) return setError('Le mot de passe doit contenir au moins 8 caractères.');
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setResetDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Lien invalide ou expiré.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 20px 60px' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* En-tête */}
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: 12, backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <KeyRound style={{ width: 24, height: 24, color: '#374151' }} />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>
            {token ? 'Nouveau mot de passe' : 'Mot de passe oublié'}
          </h1>
          <p style={{ color: '#6B7280', fontSize: '0.9375rem', margin: 0 }}>
            {token ? 'Choisissez un nouveau mot de passe sécurisé.' : 'Indiquez votre adresse e-mail pour recevoir un lien de réinitialisation.'}
          </p>
        </div>

        <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: 12, padding: '28px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

          {/* ── Mode demande — succès ── */}
          {requestSent && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
              <div style={{ width: 52, height: 52, backgroundColor: '#D1FAE5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Mail style={{ width: 24, height: 24, color: '#059669' }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.0625rem', color: '#111827', marginBottom: 6 }}>E-mail envoyé !</div>
                <p style={{ color: '#6B7280', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                  Si <strong style={{ color: '#374151' }}>{email}</strong> correspond à un compte, vous recevrez un lien valable <strong>2 heures</strong> pour choisir un nouveau mot de passe.
                </p>
              </div>
              <div style={{ fontSize: '0.8125rem', color: '#9CA3AF', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 14px', width: '100%' }}>
                Vérifiez également vos courriers indésirables.
              </div>
              <button onClick={() => navigate('/auth')} style={{ ...btnPrimary, marginTop: 4 }}>
                Retour à la connexion
              </button>
            </div>
          )}

          {/* ── Mode demande — formulaire ── */}
          {!token && !requestSent && (
            <form onSubmit={handleRequestReset} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <button type="button" onClick={() => navigate('/auth')} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '0.8125rem', padding: 0, marginBottom: 4 }}>
                <ArrowLeft style={{ width: 13, height: 13 }} /> Retour
              </button>
              {error && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: '0.875rem' }}>
                  <AlertCircle style={{ width: 16, height: 16, flexShrink: 0, marginTop: 1 }} />{error}
                </div>
              )}
              <div>
                <label style={labelS}>Adresse e-mail</label>
                <input
                  type="email" required autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  style={inp} placeholder="votre.email@example.com"
                />
              </div>
              <button type="submit" disabled={loading} style={{ ...btnPrimary, marginTop: 4, opacity: loading ? 0.7 : 1 }}>
                {loading ? <Spinner /> : 'Recevoir le lien de réinitialisation'}
              </button>
            </form>
          )}

          {/* ── Mode reset — succès ── */}
          {token && resetDone && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
              <div style={{ width: 52, height: 52, backgroundColor: '#D1FAE5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle2 style={{ width: 26, height: 26, color: '#059669' }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.0625rem', color: '#111827', marginBottom: 6 }}>Mot de passe mis à jour !</div>
                <p style={{ color: '#6B7280', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                  Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                </p>
              </div>
              <button onClick={() => navigate('/auth')} style={{ ...btnPrimary, marginTop: 4 }}>
                Se connecter
              </button>
            </div>
          )}

          {/* ── Mode reset — formulaire ── */}
          {token && !resetDone && (
            <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {error && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: '0.875rem' }}>
                  <AlertCircle style={{ width: 16, height: 16, flexShrink: 0, marginTop: 1 }} />{error}
                </div>
              )}
              <div>
                <label style={labelS}>Nouveau mot de passe <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(min. 8 caractères)</span></label>
                <input
                  type="password" required minLength={8} autoComplete="new-password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  style={inp} placeholder="••••••••"
                />
              </div>
              <div>
                <label style={labelS}>Confirmer le mot de passe</label>
                <input
                  type="password" required minLength={8} autoComplete="new-password"
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  style={{ ...inp, borderColor: confirm && confirm !== password ? '#FCA5A5' : '#D1D5DB' }}
                  placeholder="••••••••"
                />
                {confirm && confirm !== password && (
                  <span style={{ fontSize: '0.75rem', color: '#DC2626', marginTop: 4, display: 'block' }}>Les mots de passe ne correspondent pas.</span>
                )}
              </div>
              <button type="submit" disabled={loading} style={{ ...btnPrimary, marginTop: 4, opacity: loading ? 0.7 : 1 }}>
                {loading ? <Spinner /> : 'Enregistrer le nouveau mot de passe'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
